"use client";

import { useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Typography } from "@/components/ui/typography";
import StorySectionSlide from "./StorySectionSlide";
import { StoryGroup } from "@/types/story";
import { SessionManager } from "@/lib/session/SessionManager";
import { cn } from "@/lib/utils";

interface StorySectionProps {
  storyGroups: StoryGroup[];
}

/**
 * Story bubbles section - displays story group avatars in a carousel
 * Clicking opens the story modal
 */
export default function StorySection({ storyGroups }: StorySectionProps) {
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const [emblaRef] = useEmblaCarousel({
    direction: "rtl",
    align: "start",
    containScroll: false,
    loop: true,
    dragFree: true,
  });

  // Track when story is opened
  const handleStoryOpen = (groupId: string) => {
    // Create or continue story session
    const sessionId = SessionManager.startStorySession(groupId);
    console.log(`📖 Story opened: ${groupId} | Session: ${sessionId}`);
    setOpenGroupId(groupId);
  };

  if (!storyGroups || storyGroups.length === 0) return null;

  return (
    <>
      {/* Story Bubbles Carousel */}
      <div
        ref={emblaRef}
        className="overflow-hidden w-full !pr-5 md:!pr-0 mb-4 md:mb-14"
      >
        <div
          className={cn(
            "flex gap-4",
            // Responsive slide widths
            "[&>div]:flex-[0_0_64px] [&>div]:min-w-0",
            "sm:[&>div]:flex-[0_0_calc(100%/5.5)]",
            "md:[&>div]:flex-[0_0_calc(100%/7.5)]",
            "lg:[&>div]:flex-[0_0_calc(100%/9.5)]",
            "xl:[&>div]:flex-[0_0_calc(100%/11-22px)] xl:gap-6"
          )}
        >
          {storyGroups.map((group) => (
            <div
              key={group.id}
              className="cursor-pointer text-center"
              onClick={() => handleStoryOpen(group.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleStoryOpen(group.id);
                }
              }}
            >
              <div className="flex flex-col items-center gap-[18px] md:p-1">
                <Avatar className="size-16 md:size-[5.25rem] p-0.5 bg-gradient-to-b from-primary to-secondary transition-transform hover:scale-105">
                  <AvatarImage
                    className="rounded-full border-2 md:border-4 border-background"
                    src={group.image.fileKey}
                    alt={group.image.altText ?? group.title}
                  />
                </Avatar>
                <Typography variant="captionRegular" className="text-black-300">
                  {group.title}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Story Modal */}
      <StorySectionSlide
        openGroupId={openGroupId}
        setOpenGroupId={setOpenGroupId}
      />
    </>
  );
}
